import React, { useState, useEffect, useRef } from "react";

import Select from "react-select";
import { Trans } from "react-i18next";
import EditableImage from "../../components/Images/EditableImages";
import ValidationError from "../Shared/ValidationError";
import { getRoles } from "../../utils/OktaHelpers";
import { isAuthorized } from "../../utils/RolesHelpers";
import {
    getReactSelectStyling,
    getReactSelectTheme,
} from "../../utils/ReactSelectStyles";
import { FIELD_TYPES } from "../../utils/constants";

const Form = ({
    fields,
    input,
    setInput,
    error,
    isFormSubmitted,
    image,
    inlineRender,
    addResetButton = true,
    hardReset = false,
    hardResetHandler = null,
}) => {
    const [roles, setRoles] = useState([]);

    useEffect(() => {
        setRoles(getRoles());
    }, []);

    const passwordRefs = {
        password: useRef(),
        passwordConfirmation: useRef(),
    };

    const handleInputChange = (e, fieldType) => {
        setInput({
            ...input,
            [e.target.name]:
                fieldType === "number" ? +e.target.value : e.target.value,
        });
    };

    const handleSelectChange = (selection, action, isMulti) => {
        let inputValue = {};
        if (isMulti) inputValue = selection;
        else inputValue = action.action === "clear" ? null : selection.value;
        setInput((prevInput) => ({
            ...prevInput,
            [action.name]: inputValue,
        }));
    };

    const resetForm = () => {
        if (hardReset) {
            if (hardResetHandler) hardResetHandler();
            else setInput({});
            return;
        }
        let modifiedInput = { ...input };
        fields &&
            fields.map((field) => {
                switch (field.type) {
                    case "number":
                        modifiedInput = { ...modifiedInput, [field.name]: 0 };
                        break;
                    case "date":
                        modifiedInput = {
                            ...modifiedInput,
                            [field.name]: null,
                        };
                        break;
                    default:
                        modifiedInput = { ...modifiedInput, [field.name]: "" };
                }
            });
        modifiedInput.image = "";
        setInput(modifiedInput);
    };

    const getAvatar = () => {
        return image || "no-avatar";
    };

    const handleFilterSelectOptions = (option, inputValue) => {
        //inputValue (searching options) exists in options
        const searchFound =
            !inputValue ||
            option?.label?.toLowerCase().includes(inputValue?.toLowerCase());

        const isOptionAuthorized =
            !option?.data?.roles || isAuthorized(option?.data?.roles, roles);

        return searchFound && isOptionAuthorized;
    };

    const dropDownHandler = (handler, selection, action, isMulti) => {
        if (handler) handler(selection);
        else handleSelectChange(selection, action, isMulti);
    };

    const dropdownOption = (field, index) => {
        return (
            <div
                key={index}
                className={`input-group ${
                    error[field.name] && isFormSubmitted && !field.disabled
                        ? "invalid-warning-input"
                        : ""
                }`}
            >
                <label
                    htmlFor={`dynamic-input-${index + 1}`}
                    className={`uppercase ${field.required ? "required" : ""}`}
                >
                    {field.label}
                </label>
                <Select
                    isClearable={field.isClearable}
                    isSearchable
                    isMulti={field.isMulti}
                    name={field.name}
                    onChange={(selection, action) =>
                        dropDownHandler(
                            field.handler,
                            selection,
                            action,
                            field.isMulti
                        )
                    }
                    options={field.options}
                    filterOption={
                        field.authorizeOptions
                            ? handleFilterSelectOptions
                            : undefined
                    }
                    getOptionValue={(option) =>
                        option.searchable || option.value
                    }
                    value={
                        field.defaultValue
                            ? input[field.name]
                            : field.options.find(
                                  (o) => o.value === input[field.name]
                              ) || ""
                    }
                    styles={getReactSelectStyling({
                        showError: isFormSubmitted && error[field.name],
                        isMultiSelect: true,
                    })}
                    theme={getReactSelectTheme}
                    isDisabled={field.disabled}
                    placeholder={field.placeholder || ""}
                />
                {isFormSubmitted && !field.disabled && (
                    <ValidationError errorMessage={error[field.name]} />
                )}
            </div>
        );
    };
    const fileOption = (field, index) => {
        return (
            <div key={index} className="input-group">
                <label
                    htmlFor={`dynamic-input-${index + 1}`}
                    className={`uppercase ${field.required ? "required" : ""}`}
                >
                    {field.label}
                </label>
                <input
                    type={"file"}
                    name={field.name}
                    accept="image/*"
                    disabled={field.disabled}
                />
            </div>
        );
    };

    const togglePassword = (fieldName) => {
        const children = passwordRefs[fieldName].current.children;
        // children[1] referes to the icon inside the input group
        if (children[0].type === "text") {
            children[0].type = "password";
            children[1].classList.replace("fa-eye-slash", "fa-eye");
        } else if (children[0].type === "password") {
            children[0].type = "text";
            children[1].classList.replace("fa-eye", "fa-eye-slash");
        }
    };

    const defaultOption = (field, index) => {
        return (
            <div
                key={index}
                className={`input-group ${field.disabled ? "hide" : ""} ${
                    error[field.name] && isFormSubmitted && !field.disabled
                        ? "invalid-warning-input"
                        : ""
                }`}
            >
                <label
                    htmlFor={`dynamic-input-${index + 1}`}
                    className={`uppercase ${field.required ? "required" : ""}`}
                >
                    {field.label}
                </label>
                <div className="flex" ref={passwordRefs[field.name] || null}>
                    <input
                        type={field.type || "text"}
                        name={field.name}
                        value={input[field.name] || ""}
                        onChange={(e) => handleInputChange(e, field.type)}
                        placeholder={field.placeholder}
                        min={field.min && field.min}
                        className={`input-element ${
                            field.disabled ? "disabled" : ""
                        }`}
                        style={{
                            textTransform: field.isUpperCase ? "uppercase" : "",
                        }}
                        disabled={field.disabled}
                        {...(passwordRefs[field.name] && {
                            onPaste: (e) => e.preventDefault(),
                        })}
                    />
                    <i
                        className={`fas fa-eye fa-xs -ml-6 mt-auto mb-auto cursor-pointer ${
                            field.showPasswordIcon ? "" : "hidden"
                        }`}
                        onClick={() => togglePassword(field.name)}
                    ></i>
                </div>
                {isFormSubmitted && !field.disabled && (
                    <ValidationError errorMessage={error[field.name]} />
                )}
            </div>
        );
    };

    const rednerOutputForm = () => {
        return (
            <div className="main-customer-form-inputs grid-3-cols">
                {fields &&
                    fields.map((field, index) => {
                        if (!field.role) {
                            if (field.type === FIELD_TYPES.Dropdown) {
                                return dropdownOption(field, index);
                            } else if (field.type === "file") {
                                return fileOption(field, index);
                            } else {
                                return defaultOption(field, index);
                            }
                        }
                    })}
            </div>
        );
    };
    return (
        <>
            <EditableImage
                state={input}
                stateSetter={setInput}
                defaultImage={
                    require(`../../assets/images/${getAvatar()}.png`).default
                }
                renderInline={() => (
                    <div className="w-full mb-10 pb-5">
                        {rednerOutputForm()}
                        {inlineRender && inlineRender()}
                    </div>
                )}
            />

            {addResetButton && (
                <button
                    onClick={resetForm}
                    className="secondary-blue-btn ml-2 float-right"
                >
                    <Trans i18nKey="reset">Reset</Trans>
                </button>
            )}
        </>
    );
};

export default Form;
